import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AuthContextType, useAuth } from "../../auth/auth-provider";
import { CannedMessageIcon, Smile, spaik } from "../../assets";
import { Button, useToast } from "../../components";
import useCannedMessage, { CannedMessageItem } from "./use-cannedMessage";
import {
  Container,
  CreateForm,
  CreateAndFilter,
  DeleteConfirmActions,
  DeleteConfirmContent,
  EmptyState,
  FormInput,
  FormTextarea,
  MessageCard,
  MessagesList,
  ShortcutInput
} from "./styles";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  setEmojiModalOpen,
  setEmojiTarget,
  textEmoji
} from "../../redux/slices/flowInteractions";
import { EmojiModal } from "../Interactions/components/interacaoRobo/EmojiModal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { v } from "../../util";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";

interface CannedMessageProps {
  messages: CannedMessageItem[];
  search: string;
  onSearchChange: (value: string) => void;
  hasMore?: boolean;
  loading?: boolean;
  onLoadMore?: () => void;
  onCreateSuccess?: (message: CannedMessageItem) => void;
  onUpdateSuccess?: (message: CannedMessageItem) => void;
  onDeleteSuccess?: (id: number) => void;
  onModeChange?: (mode: "list" | "create" | "edit") => void;
}

interface CannedMessageFormData {
  shortcut: string;
  title: string;
  finalizeDialog: boolean;
  message: string;
}

const cannedMessageSchema = v.object({
  shortcut: v
    .string()
    .refine((value) => value.trim() !== "", {
      message: "Atalho é obrigatório."
    })
    .refine((value) => value.trim().length <= 50, {
      message: "Atalho deve ter no máximo 50 caracteres."
    }),
  title: v
    .string()
    .refine((value) => value.trim() !== "", {
      message: "Título é obrigatório."
    })
    .refine((value) => value.trim().length <= 255, {
      message: "Título deve ter no máximo 255 caracteres."
    }),
  finalizeDialog: v.boolean(),
  message: v
    .string()
    .refine((value) => value.trim() !== "", {
      message: "Mensagem é obrigatória."
    })
    .refine((value) => value.trim().length <= 1000, {
      message: "Mensagem deve ter no máximo 1000 caracteres."
    })
});

export const CannedMessage = ({
  messages,
  search,
  onSearchChange,
  hasMore = false,
  loading = false,
  onLoadMore,
  onCreateSuccess,
  onUpdateSuccess,
  onDeleteSuccess,
  onModeChange
}: CannedMessageProps) => {
  const { user } = useAuth() as AuthContextType;
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { createCannedMessage, updateCannedMessage, deleteCannedMessage } =
    useCannedMessage();
  const emoji = useAppSelector(
    (state) => state.flowInteractions.valueTextEmoji
  );
  const emojiTarget = useAppSelector(
    (state) => state.flowInteractions.emojiTarget
  );
  const modalEdit = useAppSelector((state) => state.omnichannel.modalEdit);
  const [isCreating, setIsCreating] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [messageIdToDelete, setMessageIdToDelete] = useState<number | null>(
    null
  );
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const messageRef = useRef<HTMLTextAreaElement | null>(null);
  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<CannedMessageFormData>({
    defaultValues: {
      shortcut: "",
      title: "",
      finalizeDialog: false,
      message: ""
    },
    resolver: zodResolver(cannedMessageSchema),
    mode: "onChange",
    reValidateMode: "onChange"
  });
  const shortcut = watch("shortcut");
  const title = watch("title");
  const finalizeDialog = watch("finalizeDialog");
  const message = watch("message");

  const adjustTextareaHeight = () => {
    const textarea = messageRef.current;

    if (!textarea) return;

    textarea.style.height = "124px";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const resetCreateForm = () => {
    reset({
      shortcut: "",
      title: "",
      finalizeDialog: false,
      message: ""
    });
    setCursorPosition(0);
    dispatch(textEmoji(""));
    dispatch(setEmojiTarget(""));
    if (messageRef.current) {
      messageRef.current.style.height = "124px";
    }
  };

  const handleCancelCreate = () => {
    resetCreateForm();
    setEditingMessageId(null);
    setIsCreating(false);
  };

  const openCreateForm = () => {
    resetCreateForm();
    setEditingMessageId(null);
    setIsCreating(true);
    onModeChange?.("create");
  };

  const openEditForm = (item: CannedMessageItem) => {
    reset({
      shortcut: item.shortcut,
      title: item.title,
      finalizeDialog: item.finalizeDialog,
      message: item.message
    });
    setEditingMessageId(item.id);
    setCursorPosition(item.message.length);
    setIsCreating(true);
    onModeChange?.("edit");
  };

  const handleSaveCannedMessage = handleSubmit(async (data) => {
    try {
      const payload = {
        shortcut: data.shortcut.trim(),
        title: data.title.trim(),
        finalizeDialog: data.finalizeDialog,
        message: data.message.trim()
      };

      if (editingMessageId) {
        await updateCannedMessage({
          id: editingMessageId,
          ...payload
        });

        onUpdateSuccess?.({
          id: editingMessageId,
          ...payload
        });

        toast?.current?.show({
          life: 3000,
          severity: "success",
          summary: "Sucesso",
          detail: "Mensagem pronta atualizada com sucesso."
        });
      } else {
        const response = await createCannedMessage(payload);

        onCreateSuccess?.({
          id: response.id,
          ...payload
        });

        toast?.current?.show({
          life: 3000,
          severity: "success",
          summary: "Sucesso",
          detail: "Mensagem pronta criada com sucesso."
        });
      }

      handleCancelCreate();
    } catch (error) {
      const backendMessage =
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        `Não foi possível ${
          editingMessageId ? "atualizar" : "criar"
        } a mensagem pronta.`;

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  });

  const handleDeleteCannedMessage = async () => {
    if (!messageIdToDelete) return;

    try {
      await deleteCannedMessage(messageIdToDelete);

      onDeleteSuccess?.(messageIdToDelete);

      setConfirmDeleteModal(false);
      setMessageIdToDelete(null);

      toast?.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso",
        detail: "Mensagem pronta excluída com sucesso."
      });
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Erro ao excluir mensagem pronta.";

      setConfirmDeleteModal(false);
      setMessageIdToDelete(null);

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  useEffect(() => {
    if (!emoji || emojiTarget !== "canned-message") return;

    const text = String(message ?? "");
    const start = Math.max(0, Math.min(cursorPosition, text.length));
    const first = text.slice(0, start);
    const last = text.slice(start);
    const updatedValue = `${first}${emoji}${last}`.slice(0, 1000);

    setValue("message", updatedValue, {
      shouldValidate: true,
      shouldDirty: true
    });

    dispatch(textEmoji(""));
    dispatch(setEmojiTarget(""));
  }, [cursorPosition, dispatch, emoji, emojiTarget, message, setValue]);

  useLayoutEffect(() => {
    if (!isCreating) return;

    const timer = window.setTimeout(() => {
      adjustTextareaHeight();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [isCreating, message]);

  useEffect(() => {
    if (!isCreating) {
      onModeChange?.("list");
    }
  }, [isCreating, onModeChange]);

  const handleMessagesScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - scrollTop <= clientHeight + 10) {
      onLoadMore?.();
    }
  };

  return (
    <Container>
      <Dialog
        visible={confirmDeleteModal}
        onHide={() => {
          setConfirmDeleteModal(false);
          setMessageIdToDelete(null);
        }}
        draggable={false}
        header="Deseja excluir a mensagem?"
        style={{ width: "575px" }}
      >
        <DeleteConfirmContent>
          <div>
            Ao excluir a mensagem, essa mensagem será removida
            permanentemente e essa ação não poderá ser desfeita. Tem certeza
            que deseja continuar?
          </div>

          <DeleteConfirmActions>
            <Button
              label="Sim"
              className="p-button-danger"
              onClick={handleDeleteCannedMessage}
            />
            <Button
              label="Não"
              className="p-button-secondary"
              onClick={() => {
                setConfirmDeleteModal(false);
                setMessageIdToDelete(null);
              }}
            />
          </DeleteConfirmActions>
        </DeleteConfirmContent>
      </Dialog>

      <div className="header">
        <img src={user.plataform.urlLogo ? user.plataform.urlLogo : spaik} />
        <div className="description">
          <div>Mensagens prontas</div>
          <div className="text">Atalhos para acelerar seu atendimento</div>
        </div>
      </div>

      {isCreating ? (
        <CreateForm>
          <div className="fields">
            <div className="field">
              <div className="label">Atalho</div>
              <div className="shortcutWrapper">
                <div className="shortcutPrefix">/</div>
                <ShortcutInput
                  value={shortcut}
                  placeholder="Digite o atalho para acessar a mensagem"
                  onChange={(e) =>
                    setValue("shortcut", e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true
                    })
                  }
                />
              </div>
              {errors.shortcut?.message ? (
                <div style={{ color: "#AA3939" }}>
                  {errors.shortcut.message}
                </div>
              ) : null}
            </div>

            <div className="field">
              <div className="label">Título</div>
              <FormInput
                value={title}
                placeholder="Digite o título da mensagem"
                onChange={(e) =>
                  setValue("title", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true
                  })
                }
              />
              {errors.title?.message ? (
                <div style={{ color: "#AA3939" }}>{errors.title.message}</div>
              ) : null}
            </div>

            <div className="field">
              <label
                htmlFor="finalize-dialog"
                className="checkboxField p-checkbox-label"
              >
                <Checkbox
                  inputId="finalize-dialog"
                  checked={finalizeDialog}
                  onChange={(e) =>
                    setValue("finalizeDialog", !!e.checked, {
                      shouldValidate: true,
                      shouldDirty: true
                    })
                  }
                />
                <span>Finalizar atendimento após envio da mensagem?</span>
              </label>
            </div>

            <div className="field fieldMessage">
              <div className="label">Mensagem</div>
              <div className="textareaWrapper">
                <FormTextarea
                  ref={messageRef}
                  value={message}
                  placeholder="Digite a mensagem"
                  onChange={(e) =>
                    setValue("message", e.target.value.slice(0, 1000), {
                      shouldValidate: true,
                      shouldDirty: true
                    })
                  }
                  onInput={(e) => {
                    const target = e.currentTarget as HTMLTextAreaElement;
                    target.style.height = "124px";
                    target.style.height = `${target.scrollHeight}px`;
                  }}
                  onKeyUp={(e) =>
                    setCursorPosition(e.currentTarget.selectionStart ?? 0)
                  }
                  onClick={(e) =>
                    setCursorPosition(e.currentTarget.selectionStart ?? 0)
                  }
                  onSelect={(e) =>
                    setCursorPosition(e.currentTarget.selectionStart ?? 0)
                  }
                  maxLength={1000}
                  autoResize={false}
                />

                <div className="footerActions">
                  <div className="characterCount">{message.length}/1000</div>

                  <button
                    type="button"
                    className="emojiButton"
                    onClick={() => {
                      dispatch(setEmojiTarget("canned-message"));
                      dispatch(setEmojiModalOpen(true));
                    }}
                  >
                    <img src={Smile} alt="Emoji" style={{ width: "18px" }} />
                  </button>
                </div>
              </div>
              {errors.message?.message ? (
                <div style={{ color: "#AA3939" }}>{errors.message.message}</div>
              ) : null}
            </div>
          </div>

          <div className="actions">
            <Button
              label="Salvar"
              style={{
                backgroundColor: user.plataform.mainColor || "#2624CD",
                display: "flex",
                width: "150px",
                borderRadius: "8px",
                borderColor: "#d2d2d2"
              }}
              onClick={handleSaveCannedMessage}
            />

            <Button
              label="Cancelar"
              style={{
                display: "flex",
                width: "150px",
                borderRadius: "8px",
                backgroundColor: "#d2d2d2",
                borderColor: "#d2d2d2"
              }}
              onClick={handleCancelCreate}
            />
          </div>

          {modalEdit && emojiTarget === "canned-message" && (
            <EmojiModal heightEmojiPicker="375px" />
          )}
        </CreateForm>
      ) : (
        <>
          <CreateAndFilter>
            <div className="divInput">
              <i className="pi pi-search" />

              <input
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Buscar"
              />
            </div>

            <Button
              className="buttonCreate"
              style={{
                backgroundColor: user.plataform.mainColor || "#2624CD",
                borderColor: user.plataform.mainColor || "#2624CD"
              }}
              onClick={openCreateForm}
            >
              <i className="pi pi-plus" />
              <div>Nova</div>
            </Button>
          </CreateAndFilter>

          <MessagesList onScroll={handleMessagesScroll}>
            {messages.length ? (
              messages.map((item) => (
                <MessageCard
                  key={item.id}
                  type="button"
                  onClick={() => openEditForm(item)}
                >
                  <div className="symbol">
                    <img src={CannedMessageIcon} alt="Mensagem pronta" />
                  </div>

                  <div className="content">
                    <div className="topLine">
                      <div className="name">{item.title}</div>
                      <div className="shortcut">/{item.shortcut}</div>
                    </div>

                    <div className="message">{item.message}</div>
                  </div>

                  <div className="divTrash">
                    <div
                      className="trashAction"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMessageIdToDelete(item.id);
                        setConfirmDeleteModal(true);
                      }}
                    >
                      <i className="pi pi-trash" />
                    </div>
                  </div>
                </MessageCard>
              ))
            ) : (
              <EmptyState>
                {loading
                  ? "Carregando mensagens prontas..."
                  : "Nenhuma mensagem pronta encontrada."}
              </EmptyState>
            )}
          </MessagesList>
        </>
      )}
    </Container>
  );
};
